<template>
  <div class="video-wrapper" :class="{ 'user-inactive': !userActive }" id="videoWrapper" @click.stop>
    <!-- Video Cover -->
    <div class="video-cover" v-if="showCover" @click="startFromCover" :style="{ opacity: coverOpacity }">
      <img :src="`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`" alt="Video Cover" />
      <button @click.stop="startFromCover">
        <svg width="60" height="60" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M170 96c-24 0-42 18-42 42v236c0 24 18 42 42 42 9 0 18-3 25-8l196-118c21-13 21-37 0-50L195 104c-7-5-16-8-25-8z"
                stroke="white" stroke-width="32" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>

    <!-- Loading overlay -->
    <div class="loading-overlay" v-show="loading">
      <div class="loading-spinner"></div>
    </div>

    <!-- Iframe -->
    <iframe
        id="player"
        ref="playerEl"
        :src="embedSrc"
        allow="autoplay; encrypted-media"
        allowfullscreen
        frameborder="0"
        @load="onIframeLoad"
    ></iframe>

    <!-- Pause overlay -->
    <div class="pause-overlay" :class="{ show: !isPlaying && userActive && !hideUI }" @click.stop="playVideo">
      <button @click.stop="playVideo">
        <svg width="120" height="120" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M170 96c-24 0-42 18-42 42v236c0 24 18 42 42 42 9 0 18-3 25-8l196-118c21-13 21-37 0-50L195 104c-7-5-16-8-25-8z"
                stroke="white" stroke-width="32" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>

    <!-- Skip indicators -->
    <div class="skip-indicator skip-left" :class="{ show: showBack && userActive && !hideUI }">-10s</div>
    <div class="skip-indicator skip-right" :class="{ show: showForward && userActive && !hideUI }">+10s</div>

    <!-- Center hover icons -->
    <div class="hover-controls" :class="{ visible: isPlaying && userActive && !hideUI }">
      <div class="hover-icon" @click.stop="skip(-10)">
        <svg width="80" height="80" viewBox="0 0 256 256" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g fill="#FFFFFF">
            <path d="M128 28c55 0 100 45 100 100h-16c0-46.4-37.6-84-84-84-21.9 0-41.9 8.2-57.2 21.7l12 12c2.5 2.5 1.1 6.8-2.4 7.5l-41.9 8.6c-3.3.7-6.2-2.2-5.5-5.5l8.6-41.9c.7-3.5 5-4.9 7.5-2.4l11.9 11.9C78.6 37.8 102.2 28 128 28z"/>
            <path d="M128 228c-55 0-100-45-100-100h16c0 46.4 37.6 84 84 84s84-37.6 84-84h16c0 55-45 100-100 100z"/>
            <path d="M104 154v-52l-16 10" stroke="white" stroke-width="14" stroke-linecap="round" stroke-linejoin="round"/>
            <rect x="128" y="100" width="36" height="58" rx="18" fill="#ffffff00" stroke="white" stroke-width="14"/>
          </g>
        </svg>
      </div>

      <div class="hover-icon" @click.stop="togglePlay">
        <svg width="100" height="100" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="210" y="176" width="36" height="160" rx="18" fill="white"/>
          <rect x="306" y="176" width="36" height="160" rx="18" fill="white"/>
        </svg>
      </div>

      <div class="hover-icon" @click.stop="skip(10)">
        <svg width="80" height="80" viewBox="0 0 256 256" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g fill="#FFFFFF">
            <path d="M128 28c-55 0-100 45-100 100h16c0-46.4 37.6-84 84-84 21.9 0 41.9 8.2 57.2 21.7l-12 12c-2.5 2.5-1.1 6.8 2.4 7.5l41.9 8.6c3.3.7 6.2-2.2 5.5-5.5l-8.6-41.9c-.7-3.5-5-4.9-7.5-2.4l-11.9 11.9C177.4 37.8 153.8 28 128 28z"/>
            <path d="M128 228c55 0 100-45 100-100h-16c0 46.4-37.6 84-84 84s-84-37.6-84-84H28c0 55 45 100 100 100z"/>
            <path d="M104 154v-52l-16 10" stroke="white" stroke-width="14" stroke-linecap="round" stroke-linejoin="round"/>
            <rect x="128" y="100" width="36" height="58" rx="18" fill="#ffffff00" stroke="white" stroke-width="14"/>
          </g>
        </svg>
      </div>
    </div>

    <!-- Bottom controls -->
    <div class="overlay" :class="{ hidden: !userActive || hideUI }" @mousemove.stop="resetInactivity">
      <div class="controls">
        <button @click.stop="togglePlay">{{ isPlaying ? '⏸️' : '▶️' }}</button>
        <input
            type="range"
            min="0"
            max="100"
            step="0.1"
            v-model.number="progressValue"
            @input="onProgressInput"
            :style="progressStyle"
        />
        <div class="time-display">{{ currentTimeText }} / {{ durationText }}</div>
        <select v-model.number="playbackRate" @change="onRateChange">
          <option :value="0.5">0.5x</option>
          <option :value="1">1x</option>
          <option :value="1.5">1.5x</option>
          <option :value="2">2x</option>
        </select>
        <button @click="toggleMute">{{ isMuted ? '🔇' : '🔊' }}</button>
        <button @click="enterFullscreen">⛶</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CustomYouTubePlayer',
  data() {
    return {
      // State
      player: null,
      isPlaying: false,
      isMuted: false,
      duration: 0,
      progressValue: 0,
      playbackRate: 1,
      // UI flags
      showBack: false,
      showForward: false,
      loading: false,
      // Cover
      showCover: true,
      coverOpacity: 1,
      // internals
      _interval: null,
      _hideBackTimeout: null,
      _hideFwdTimeout: null,
      currentTime: 0,
      hideUI: false,
      userActive: true,
      _inactivityTimer: null,
      _lastTime: 0,
    };
  },
  props: {
    videoId: String,
    type: String,
  },
  computed: {
    embedSrc() {
      return `https://www.youtube.com/embed/${this.videoId}?enablejsapi=1&modestbranding=1&rel=0&controls=0&showinfo=0&iv_load_policy=3&disablekb=1`;
    },
    currentTimeText() {
      return this.formatTime(this.currentTime);
    },
    durationText() {
      return this.formatTime(this.duration);
    },
    progressStyle() {
      const v = this.progressValue || 0;
      return {
        background: `linear-gradient(to right, #00adee 0%, #00adee ${v}%, rgba(255,255,255,0.4) ${v}%, rgba(255,255,255,0.4) 100%)`
      };
    }
  },
  mounted() {
    // Load YT API
    if (!window.YT || !window.YT.Player) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(tag);
      window.onYouTubeIframeAPIReady = this.createPlayer;
    } else {
      this.createPlayer();
    }

    // Keyboard + double click
    window.addEventListener('keydown', this.onKey);
    const wrapper = this.$el;
    wrapper.addEventListener('dblclick', this.onDblClick);

    // Mouse movement tracking
    const reset = this.resetInactivity;
    document.addEventListener('mousemove', reset, { passive: true });
    document.addEventListener('mousedown', reset, { passive: true });
    document.addEventListener('keydown', reset);
    document.addEventListener('touchstart', reset, { passive: true });

    // Fullscreen lifecycle
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
    document.addEventListener('msfullscreenchange', this.onFullscreenChange);

    // Start inactivity timer
    this.armInactivity();
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.onKey);
    const wrapper = this.$el;
    wrapper.removeEventListener('dblclick', this.onDblClick);

    const reset = this.resetInactivity;
    document.removeEventListener('mousemove', reset);
    document.removeEventListener('mousedown', reset);
    document.removeEventListener('keydown', reset);
    document.removeEventListener('touchstart', reset);

    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
    document.removeEventListener('msfullscreenchange', this.onFullscreenChange);

    clearTimeout(this._inactivityTimer);
    clearInterval(this._interval);
  },

  methods: {
    createPlayer() {
      this.player = new window.YT.Player('player', {
        events: {
          onReady: this.onPlayerReady,
          onStateChange: this.onPlayerStateChange,
          onError: this.onPlayerError
        }
      });
    },

    onIframeLoad() {
      // Keep for any iframe load side-effects if needed
    },

    onPlayerReady() {
      this.duration = this.player.getDuration ? (this.player.getDuration() || 0) : 0;
      if (this._interval) clearInterval(this._interval);
      this._interval = setInterval(() => {
        if (!this.player) return;
        const total = this.player.getDuration ? this.player.getDuration() : 0;
        if (total > 0) this.duration = total;
        if (this.isPlaying) {
          const current = this.player.getCurrentTime ? this.player.getCurrentTime() : 0;
          this.currentTime = current;
          this._lastTime = current;
          const pct = total > 0 ? (current / total) * 100 : 0;
          this.progressValue = pct || 0;
        }
      }, 500);
    },

    onPlayerStateChange(event) {
      const YTS = window.YT.PlayerState;
      if (event.data === YTS.PLAYING) {
        this.isPlaying = true;
      } else if (event.data === YTS.PAUSED || event.data === YTS.ENDED) {
        this.isPlaying = false;
      }
    },

    onPlayerError() {
      this.isPlaying = false;
    },

    formatTime(s) {
      s = Math.floor(s || 0);
      const m = Math.floor(s / 60);
      const r = (s % 60).toString().padStart(2, '0');
      return `${m}:${r}`;
    },

    playVideo() {
      if (!this.player) return;
      try { this.player.playVideo && this.player.playVideo(); } catch {}
    },

    pauseVideo() {
      if (!this.player) return;
      try { this.player.pauseVideo && this.player.pauseVideo(); } catch {}
    },

    togglePlay() {
      if (this.isPlaying) this.pauseVideo();
      else this.playVideo();
    },

    skip(seconds) {
      if (!this.player || !this.player.getCurrentTime) return;
      const current = this.player.getCurrentTime();
      const newTime = Math.max(0, Math.min(current + seconds, this.duration || current + seconds));
      this.player.seekTo && this.player.seekTo(newTime, true);

      // Indicators
      if (seconds < 0) {
        this.showBack = true;
        clearTimeout(this._hideBackTimeout);
        this._hideBackTimeout = setTimeout(() => (this.showBack = false), 600);
      } else {
        this.showForward = true;
        clearTimeout(this._hideFwdTimeout);
        this._hideFwdTimeout = setTimeout(() => (this.showForward = false), 600);
      }
    },

    onProgressInput(e) {
      if (!this.player || !this.duration) return;
      const pct = Number(e.target.value || 0);
      const newTime = (pct / 100) * this.duration;
      this.player.seekTo && this.player.seekTo(newTime, true);
    },

    onRateChange() {
      if (!this.player) return;
      try { this.player.setPlaybackRate && this.player.setPlaybackRate(this.playbackRate); } catch {}
    },

    toggleMute() {
      if (!this.player) return;
      if (this.isMuted) {
        this.player.unMute && this.player.unMute();
      } else {
        this.player.mute && this.player.mute();
      }
      this.isMuted = !this.isMuted;
    },

    enterFullscreen() {
      const wrapper = this.$el;

      // If already in fullscreen, exit
      if (document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement) {
        if (document.exitFullscreen) document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
        else if (document.msExitFullscreen) document.msExitFullscreen();
        return;
      }

      // Enter fullscreen
      if (wrapper.requestFullscreen) wrapper.requestFullscreen();
      else if (wrapper.webkitRequestFullscreen) wrapper.webkitRequestFullscreen();
      else if (wrapper.msRequestFullscreen) wrapper.msRequestFullscreen();
    },

    startFromCover() {
      this.coverOpacity = 0;
      setTimeout(() => {
        this.showCover = false;
        this.playVideo();
      }, 500);
    },

    onDblClick(e) {
      const target = e.target;
      const isControl = target.closest && (
          target.closest('.controls') ||
          target.closest('.pause-overlay') ||
          target.closest('.loading-overlay')
      );
      if (isControl) return;

      const rect = this.$el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      if (x < rect.width / 2) this.skip(-10);
      else this.skip(10);
    },

    onKey(e) {
      if (!this.player) return;
      if (e.key === 'ArrowRight') {
        this.skip(10);
        this.showForward = true;
        clearTimeout(this._hideFwdTimeout);
        this._hideFwdTimeout = setTimeout(() => (this.showForward = false), 600);
      } else if (e.key === 'ArrowLeft') {
        this.skip(-10);
        this.showBack = true;
        clearTimeout(this._hideBackTimeout);
        this._hideBackTimeout = setTimeout(() => (this.showBack = false), 600);
      } else if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        this.togglePlay();
      }
    },

    armInactivity() {
      clearTimeout(this._inactivityTimer);

      // Auto-hide after 2 seconds in both fullscreen and normal mode
      this._inactivityTimer = setTimeout(() => {
        this.userActive = false;
        this.hideUI = true;
      }, 2000);
    },

    resetInactivity() {
      this.userActive = true;
      this.hideUI = false;
      this.armInactivity();
    },

    onFullscreenChange() {
      // Reset UI state when fullscreen changes
      this.userActive = true;
      this.hideUI = false;
      clearTimeout(this._inactivityTimer);

      // Start 2-second inactivity timer
      this._inactivityTimer = setTimeout(() => {
        this.userActive = false;
        this.hideUI = true;
      }, 2000);
    },
  }
};
</script>

<style scoped>
/* Layout */
.video-wrapper {
  position: relative;
  width: 100%;
  max-width: 100%;
  aspect-ratio: 16/9;
  border-radius: 12px;
  overflow: hidden;
  background: black;
  cursor: pointer;
  direction: ltr;
}

.video-wrapper.user-inactive {
  cursor: none;
}

iframe {
  width: 100%;
  height: 100%;
  border: 0;
  pointer-events: none;
}

/* Overlay and controls */
.overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  background: rgba(0, 0, 0, 0.2);
  z-index: 5;
  opacity: 1;
  transition: opacity 0.3s ease;
}

.overlay.hidden {
  opacity: 0;
  pointer-events: none;
}

.controls {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 1rem;
  max-width: 100%;
  flex-wrap: wrap;
}

button, select {
  background: rgba(255, 255, 255, 0.25);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  cursor: pointer;
  padding: 8px 12px;
  transition: background 0.2s;
}

select option {
  color: #000;
}

input[type="range"] {
  flex: 1;
  appearance: none;
  height: 6px;
  border-radius: 3px;
  outline: none;
  cursor: pointer;
  background: linear-gradient(
      to right,
      #00adee 0%,
      #00adee 0%,
      rgba(255, 255, 255, 0.4) 0%,
      rgba(255, 255, 255, 0.4) 100%
  );
}

input[type="range"]::-webkit-slider-thumb {
  appearance: none;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: white;
  cursor: pointer;
  transition: 0.2s;
}

.time-display {
  color: white;
  font-size: 0.9rem;
  width: 100px;
  text-align: right;
}

/* Pause overlay */
.pause-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  opacity: 0;
  pointer-events: none;
  transform: scale(1.1);
  transition: opacity 0.4s ease, transform 0.4s ease;
}

.pause-overlay.show {
  opacity: 1;
  pointer-events: auto;
  transform: scale(1);
}

.pause-overlay button {
  font-size: 3rem;
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  transform: scale(0.8);
  transition: transform 0.4s ease;
}

.pause-overlay.show button {
  transform: scale(1);
}

/* Skip indicators */
.skip-indicator {
  position: absolute;
  top: 50%;
  font-size: 2rem;
  color: white;
  opacity: 0;
  transition: opacity 0.3s, transform 0.3s;
  transform: translateY(-50%);
  z-index: 20;
  user-select: none;
}

.skip-indicator.show {
  opacity: 1;
  transform: translateY(-50%) scale(1.3);
}

.skip-left { left: 30px; }
.skip-right { right: 30px; }

/* Video cover */
.video-cover {
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: black;
  z-index: 15;
  cursor: pointer;
  transition: opacity 0.5s ease;
}

.video-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
}

.video-cover button {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  font-size: 3rem;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  padding: 20px;
  cursor: pointer;
  transition: transform 0.3s ease;
  width: 100px;
  height: 100px;
}

.video-cover button:hover {
  transform: scale(1.2);
}

/* Loading overlay */
.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 30;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0%{transform:rotate(0deg)}
  100%{transform:rotate(360deg)}
}

/* Center hover controls */
.hover-controls {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 80px;
  z-index: 12;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.18s ease;
}

.hover-controls.visible {
  opacity: 1;
}

.hover-icon {
  font-size: 60px;
  color: white;
  user-select: none;
  cursor: pointer;
  pointer-events: auto;
  transition: transform 0.12s ease;
}

.hover-icon:active,
.hover-icon:hover {
  transform: scale(1.15);
}

/* Fullscreen adjustments */
:fullscreen .video-wrapper,
:-webkit-full-screen .video-wrapper {
  cursor: default;
}

:fullscreen .video-wrapper.user-inactive,
:-webkit-full-screen .video-wrapper.user-inactive {
  cursor: none !important;
}

:fullscreen .overlay,
:-webkit-full-screen .overlay {
  opacity: 1 !important;
  background: rgba(0, 0, 0, 0.3);
}

:fullscreen .overlay.hidden,
:-webkit-full-screen .overlay.hidden {
  opacity: 0 !important;
  pointer-events: none !important;
}

:fullscreen .controls,
:-webkit-full-screen .controls {
  position: absolute;
  bottom: 10px;
  left: 0;
  right: 0;
  padding: 1rem 2rem;
  background: rgba(0, 0, 0, 0.3);
}

:fullscreen input[type="range"],
:-webkit-full-screen input[type="range"] {
  flex: 1;
}
</style>
